import React, { useCallback } from "react";
import { Row, Col } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { useFormContext } from "react-hook-form";
import ReactHtmlParser from "react-html-parser";
import { replaceLinks } from "helpers/urlHelpers";
import usePermissions from "hooks/usePermissions";
import { replaceLinebreaks } from "helpers/commonHelpers";
import { getLabelWithTranslationFallback } from "helpers/getLabel";

const organisational = ({ state }: any) => {
  const intl = useIntl();
  const methods = useFormContext();
  const { getPaymentLocationLabel } = usePermissions();
  const { getValues } = methods;
  const { paymentLocations } = state;
  const { organizational_options } = state?.locationDetails;

  const getLabel = (field_id: string) => organizational_options && organizational_options[field_id]?.label;

  const getPaymentLocation = useCallback(() => {
    const keys = Object.keys(paymentLocations).filter((mode: any) => getValues("organizational.location")?.includes(mode));
    return keys.map((key: any) => paymentLocations[key]);
  }, [paymentLocations]);

  return (
    <div>
      <Row>
        <Col className="font-b3">
          {getValues("shipment.title") && (
            <Row className="mb-2">
              <Col xs={5} className="text-secondary">
                <FormattedMessage
                  id="general.shipmentTitle"
                  defaultMessage="Shipment Title"
                  values={{ shipmentTitle: getLabel("shipment_title") }}
                />
              </Col>
              <Col xs={7} className="text-capitalize">
                {getValues("shipment.title")}
              </Col>
            </Row>
          )}
          {getValues("organizational.location") && (
            <Row className="mb-2">
              <Col xs={5} className="text-secondary">
                {getPaymentLocationLabel()}
              </Col>
              <Col xs={7}>{getPaymentLocation()[0]?.label}</Col>
            </Row>
          )}
          {getValues("organizational.invoice_location_value") && (
            <Row className="mb-2">
              <Col xs={5} className="text-secondary">
                <FormattedMessage
                  id="new-request.quote-confirmation.organisation.invoiceLocation"
                  defaultMessage="Invoice location"
                />
              </Col>
              <Col xs={7}>{getValues("organizational.invoice_location")}</Col>
            </Row>
          )}
          {getValues("organizational.custom_long_field_01") && (
            <Row className="mb-2">
              <Col xs={5} className="text-secondary">
                {getLabelWithTranslationFallback("Custom longfield 01", {
                  translation: (
                    <FormattedMessage
                      id="new-request.quote-confirmation.organisation.customLongfield1"
                      defaultMessage="Custom longfield 1"
                    />
                  ),
                  paymentLocation: getValues("organizational.location"),
                })}
              </Col>
              <Col xs={7} className="pre-wrap">
                {ReactHtmlParser(replaceLinebreaks(replaceLinks(getValues("organizational.custom_long_field_01"))))}
              </Col>
            </Row>
          )}
          {getValues("organizational.reason_code_group") && (
            <Row className="mb-2">
              <Col xs={5} className="text-secondary">
                {getLabelWithTranslationFallback("Reason code group", {
                  translation: (
                    <FormattedMessage id="quote-request-detail.reasonCodeGroup" defaultMessage="Reason code group" />
                  ),
                  paymentLocation: getValues("organizational.location"),
                })}
              </Col>
              <Col xs={7}>{getValues("organizational.reason_code_group")}</Col>
            </Row>
          )}
          {getValues("organizational.reason_code") && (
            <Row className="mb-2">
              <Col xs={5} className="text-secondary">
                {getLabelWithTranslationFallback("Reason code", {
                  translation: <FormattedMessage id="quote-request-detail.reasonCode" defaultMessage="Reason code" />,
                })}
              </Col>
              <Col xs={7}>{getValues("organizational.reason_code")}</Col>
            </Row>
          )}
          {getValues("organizational.custom_field_01") && (
            <Row className="mb-2">
              <Col xs={5} className="text-secondary">
                {getLabelWithTranslationFallback("Custom field 01", {
                  translation: (
                    <FormattedMessage
                      id="new-request.quote-confirmation.organisation.customField1"
                      defaultMessage="Custom field 1"
                    />
                  ),
                  paymentLocation: getValues("organizational.location"),
                })}
              </Col>
              <Col xs={7}>{getValues("organizational.custom_field_01")}</Col>
            </Row>
          )}
          {getValues("organizational.custom_field_02") && (
            <Row className="mb-2">
              <Col xs={5} className="text-secondary">
                {getLabelWithTranslationFallback("Custom field 02", {
                  translation: (
                    <FormattedMessage
                      id="new-request.quote-confirmation.organisation.customField2"
                      defaultMessage="Custom field 2"
                    />
                  ),
                  paymentLocation: getValues("organizational.location"),
                })}
              </Col>
              <Col xs={7}>{getValues("organizational.custom_field_02")}</Col>
            </Row>
          )}
          {getValues("organizational.custom_field_03") && (
            <Row className="mb-2">
              <Col xs={5} className="text-secondary">
                {getLabelWithTranslationFallback("Custom field 03", {
                  translation: (
                    <FormattedMessage
                      id="new-request.quote-confirmation.organisation.customField3"
                      defaultMessage="Custom field 3"
                    />
                  ),
                  paymentLocation: getValues("organizational.location"),
                })}
              </Col>
              <Col xs={7}>{getValues("organizational.custom_field_03")}</Col>
            </Row>
          )}
          {getValues("organizational.cost_center") && (
            <Row className="mb-2">
              <Col xs={5} className="text-secondary">
                {getLabelWithTranslationFallback("Cost Center / WBS / Project #", {
                  translation: <FormattedMessage id="quote-request-detail.costCenter" defaultMessage="Cost center" />,
                  paymentLocation: getValues("organizational.location"),
                })}
              </Col>
              <Col xs={7}>{getValues("organizational.cost_center")}</Col>
            </Row>
          )}
          {getValues("organizational.cost_recovery_required") !== undefined &&
            getValues("organizational.cost_recovery_required") !== "" && (
              <Row className="mb-2">
                <Col xs={5} className="text-secondary">
                  {getLabelWithTranslationFallback("Cost recovery required", {
                    translation: (
                      <FormattedMessage
                        id="new-request.quote-confirmation.packing-list.costRecovery"
                        defaultMessage="Cost recovery required"
                      />
                    ),
                    paymentLocation: getValues("organizational.location"),
                  })}
                </Col>
                <Col xs={7}>
                  {getValues("organizational.cost_recovery_required") === "True"
                    ? intl.formatMessage({ id: "general.yes", defaultMessage: "Yes" })
                    : intl.formatMessage({ id: "general.no", defaultMessage: "No" })}
                </Col>
              </Row>
            )}
          {getValues("organizational.custom_cost_recovery_field1") && (
            <Row className="mb-2">
              <Col xs={5} className="text-secondary">
                <FormattedMessage
                  id="new-request.quote-confirmation.organisation.customCostRecoveryField1"
                  defaultMessage="Custom cost recovery field 1"
                  values={{ customCostRecoveryField1: getLabel("custom_cost_recovery_field1") }}
                />
              </Col>
              <Col xs={7}>{getValues("organizational.custom_cost_recovery_field1")}</Col>
            </Row>
          )}
          {getValues("organizational.custom_cost_recovery_field2") && (
            <Row className="mb-2">
              <Col xs={5} className="text-secondary">
                <FormattedMessage
                  id="new-request.quote-confirmation.organisation.customCostRecoveryField2"
                  defaultMessage="Custom cost recovery field 2"
                  values={{ customCostRecoveryField2: getLabel("custom_cost_recovery_field2") }}
                />
              </Col>
              <Col xs={7}>{getValues("organizational.custom_cost_recovery_field2")}</Col>
            </Row>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default organisational;
